import { normalizeStateInput } from '../registration/states';

// ─── member jurisdiction vocabulary and legacy mapping ─────────────────────
//
// A member jurisdiction is a US state on its own, with no address around it
// (ADR-130 Rule 11). It is the state input for licensing, AI availability and
// the minor derivation when the member has no `home` address row — a migrated
// member whose 1.0 address was too partial to admit (Rule 9) still has a state
// on their sponsor profile. It is never an address: nothing that ships or
// bills reads it, and it lives in its own append-only table.

/**
 * Which writer appended the row. `legacy_b2b`: copied from backend2's
 * `user_b2b_details.state` by the member migration, with `source_ref`
 * carrying the 1.0 row id so a re-run is idempotent. The array drives the
 * DB CHECK (ADR-056 Rule 8).
 */
export const MEMBER_JURISDICTION_SOURCE = ['legacy_b2b'] as const;
export type MemberJurisdictionSource = (typeof MEMBER_JURISDICTION_SOURCE)[number];

/** A stored jurisdiction is a USPS code; the pattern is the DB CHECK's source. */
export const MEMBER_JURISDICTION_STATE_PATTERN = /^[A-Z]{2}$/;

/** The parsed shape of a 1.0 `user_b2b_details` row this mapping reads. */
export interface LegacyB2bJurisdictionRow {
  readonly id: number;
  readonly state: string | null;
}

export type LegacyB2bJurisdiction =
  | { readonly kind: 'none' }
  | { readonly kind: 'skip'; readonly warning: string }
  | { readonly kind: 'write'; readonly state: string; readonly sourceRef: string };

/**
 * The jurisdiction a member's 1.0 sponsor profile gives. The current
 * `user_b2b_details` row is the highest `id`, the same row the profile
 * mapping reads. Its state is admitted only when it normalises to a USPS
 * code; anything else is skipped with a warning that names the 1.0 row and
 * the reason, never the value (ADR-130 Rule 7), so a state is never invented.
 */
export function jurisdictionFromLegacyB2b(
  rows: readonly LegacyB2bJurisdictionRow[],
): LegacyB2bJurisdiction {
  let current: LegacyB2bJurisdictionRow | undefined;
  for (const row of rows) {
    if (!current || row.id > current.id) current = row;
  }
  if (!current) return { kind: 'none' };

  const coordinate = `user_b2b_details.${current.id}`;
  const raw = current.state?.trim() ?? '';
  if (!raw) return { kind: 'skip', warning: `jurisdiction_skipped:${coordinate}.missing_state` };

  const state = normalizeStateInput(raw);
  if (!state)
    return { kind: 'skip', warning: `jurisdiction_skipped:${coordinate}.unrecognised_state` };

  return { kind: 'write', state, sourceRef: `user_b2b_details:${current.id}` };
}
